// pages/Login.js
// Login page — SaaS premium design
//
// Flow:
// 1. Mount → GET /company → logo + company name
// 2. Submit → POST /auth/login { username, password }
//    A: 200 → token in memory → navigate('/dashboard')
//    B: 403 + requires_2fa → show password2 + debug checkbox → resubmit
//    C: 401 → show error
//    D: 429 → show rate limit message
//
// Design: full-screen background (theme image/gradient) + glassmorphism form card

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/PersonOutline';
import LockIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { extractErrorMessage } from '../services/api';

// =============================================================================
// Login Page
// =============================================================================
export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { login, isAuthenticated } = useAuth();

  // ===================================================================
  // State
  // ===================================================================

  // Company info (from GET /company)
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [companyLoading, setCompanyLoading] = useState(true);

  // Form fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [debug, setDebug] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAMessage, setTwoFAMessage] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Refs for focus management
  const usernameRef = useRef(null);
  const password2Ref = useRef(null);

  // ===================================================================
  // Redirect if already authenticated
  // ===================================================================
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // ===================================================================
  // Fetch company info — public endpoint, no token
  // ===================================================================
  useEffect(() => {
    let cancelled = false;

    async function fetchCompany() {
      try {
        const response = await api.get('/company');
        if (cancelled) return;

        const data = response.data?.data || response.data;
        setCompanyName(data.company_name || '');
        setLogoUrl(data.logo_url || '');

        // Test Harness — register company data for getState()
        if (window.__MADAD_TEST__?._setCompanyData) {
          window.__MADAD_TEST__._setCompanyData(data);
        }
      } catch (err) {
        // Non-critical — show form without logo
        if (!cancelled) {
          setCompanyName('MADAD');
        }
      } finally {
        if (!cancelled) {
          setCompanyLoading(false);
        }
      }
    }

    fetchCompany();
    return () => { cancelled = true; };
  }, []);

  // ===================================================================
  // Auto-focus username on mount, password2 on 2FA reveal
  // ===================================================================
  useEffect(() => {
    if (!companyLoading && usernameRef.current) {
      usernameRef.current.focus();
    }
  }, [companyLoading]);

  useEffect(() => {
    if (requires2FA && password2Ref.current) {
      password2Ref.current.focus();
    }
  }, [requires2FA]);

  // ===================================================================
  // Submit handler
  // ===================================================================
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();

    // Validation
    if (!username.trim() || !password.trim()) {
      setError(t('login.requiredFields'));
      return;
    }

    if (requires2FA && !password2.trim()) {
      setError(t('login.password2Required'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(
        username.trim(),
        password,
        requires2FA ? password2 : undefined,
        requires2FA ? debug : undefined,
      );

      if (result.success) {
        // תרחיש A — הצלחה
        navigate('/dashboard', { replace: true });
      } else if (result.requires2FA) {
        // תרחיש B — צריך 2FA
        setRequires2FA(true);
        setTwoFAMessage(result.message || t('login.twoFARequired'));
        setError('');
      } else {
        // תרחיש C/D — שגיאה
        setError(result.error || t('login.genericError'));
      }
    } catch (err) {
      setError(extractErrorMessage(err, t('login.genericError')));
    } finally {
      setLoading(false);
    }
  }, [username, password, password2, debug, requires2FA, login, navigate, t]);

  // ===================================================================
  // Theme-based background
  // ===================================================================
  const customTheme = theme.custom || {};

  const bgStyles = {};
  if (customTheme.hasBackgroundImage) {
    // Image URL with gradient fallback in single background shorthand
    bgStyles.background = `url(${customTheme.backgroundImage}) center/cover fixed no-repeat`
      + (customTheme.backgroundFallback ? `, ${customTheme.backgroundFallback}` : '');
  } else if (customTheme.backgroundGradient) {
    bgStyles.background = customTheme.backgroundGradient;
  } else {
    // Default fallback — elegant dark gradient
    bgStyles.background = 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)';
  }

  // ===================================================================
  // Glassmorphism form card styles
  // ===================================================================
  const isDark = theme.palette.mode === 'dark';
  const glassFormStyles = {
    background: isDark
      ? 'rgba(15, 23, 42, 0.75)'
      : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: isDark
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.4)',
    boxShadow: isDark
      ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.5)',
  };

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box
      data-testid="login__page"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'rtl',
        position: 'relative',
        ...bgStyles,
      }}
    >
      {/* Background overlay for image themes */}
      {customTheme.hasBackgroundImage && customTheme.overlayColor && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: customTheme.overlayColor,
            zIndex: 0,
          }}
        />
      )}

      {/* Decorative animated gradient orbs */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${theme.palette.primary.main}33 0%, transparent 70%)`,
            top: '-10%',
            right: '-5%',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0) scale(1)' },
              '50%': { transform: 'translateY(30px) scale(1.05)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${theme.palette.secondary.main}22 0%, transparent 70%)`,
            bottom: '-5%',
            left: '-5%',
            animation: 'float2 10s ease-in-out infinite',
            '@keyframes float2': {
              '0%, 100%': { transform: 'translateY(0) scale(1)' },
              '50%': { transform: 'translateY(-20px) scale(1.08)' },
            },
          }}
        />
      </Box>

      {/* Form card */}
      <Fade in timeout={600}>
        <Paper
          data-testid="login__form-card"
          elevation={0}
          sx={{
            position: 'relative',
            zIndex: 1,
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            mx: { xs: 2, sm: 0 },
            p: { xs: 3, sm: 4.5 },
            borderRadius: { xs: 2, sm: 4 },
            ...glassFormStyles,
          }}
        >
          {/* Logo + Company name */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}
          >
            {companyLoading ? (
              <CircularProgress size={40} sx={{ mb: 2 }} />
            ) : (
              <>
                {logoUrl ? (
                  <Box
                    component="img"
                    data-testid="login__logo"
                    src={logoUrl}
                    alt={companyName}
                    sx={{
                      width: 72,
                      height: 72,
                      objectFit: 'contain',
                      mb: 1.5,
                      borderRadius: 2,
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Box
                    data-testid="login__logo"
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                      bgcolor: theme.palette.primary.main,
                      color: '#fff',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{ fontFamily: "'Rubik', sans-serif", fontWeight: 700 }}
                    >
                      M
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="h5"
                  data-testid="login__company-name"
                  sx={{
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  {companyName || 'MADAD'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('login.subtitle')}
                </Typography>
              </>
            )}
          </Box>

          {/* Error message */}
          {error && (
            <Fade in>
              <Alert
                data-testid="login__error"
                severity="error"
                sx={{ mb: 2, borderRadius: 2 }}
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {/* 2FA info message */}
          {requires2FA && twoFAMessage && (
            <Fade in>
              <Alert
                data-testid="login__alert--2fa"
                severity="info"
                icon={<VpnKeyIcon />}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                {twoFAMessage}
              </Alert>
            </Fade>
          )}

          {/* Form */}
          <Box
            component="form"
            data-testid="login__form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            {/* Username */}
            <TextField
              data-testid="login__input--username"
              inputRef={usernameRef}
              label={t('login.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoComplete="username"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={textFieldGlassStyle}
            />

            {/* Password */}
            <TextField
              data-testid="login__input--password"
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      data-testid="login__button--toggle-password"
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={textFieldGlassStyle}
            />

            {/* 2FA fields — visible only after 403 requires_2fa */}
            {requires2FA && (
              <Fade in>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Password 2 */}
                  <TextField
                    data-testid="login__input--password2"
                    inputRef={password2Ref}
                    label={t('login.password2')}
                    type={showPassword2 ? 'text' : 'password'}
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    disabled={loading}
                    autoComplete="off"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKeyIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            data-testid="login__button--toggle-password2"
                            onClick={() => setShowPassword2((p) => !p)}
                            edge="end"
                            size="small"
                            tabIndex={-1}
                            aria-label={showPassword2 ? t('login.hidePassword') : t('login.showPassword')}
                          >
                            {showPassword2 ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldGlassStyle}
                  />

                  {/* Debug checkbox */}
                  <FormControlLabel
                    data-testid="login__checkbox--debug"
                    control={
                      <Checkbox
                        checked={debug}
                        onChange={(e) => setDebug(e.target.checked)}
                        disabled={loading}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BugReportIcon fontSize="small" color="action" />
                        <Typography variant="body2">{t('login.debugMode')}</Typography>
                      </Box>
                    }
                  />
                </Box>
              </Fade>
            )}

            {/* Submit button */}
            <Button
              data-testid="login__button--submit"
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 2.5,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: `0 4px 14px ${theme.palette.primary.main}44`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('login.submit')
              )}
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
}

// =============================================================================
// Shared TextField glass style
// =============================================================================
const textFieldGlassStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    transition: 'all 0.2s ease',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
      },
    },
  },
};
